import { stripHtml } from '@/lib/homepage-mappers';
import type { ProcessNavGroup, ProcessSequenceStep } from '@/types/process';
import {
  STAGE_COLORS,
  getStageStats,
  getStageVideoSrc,
  type ProcessStageStat,
  type ProcessStageView,
} from './process-stage-constants';

type TranslateFn = (key: string) => string;

function buildStatsFromItems(items: ProcessSequenceStep['items']): ProcessStageStat[] {
  return (items ?? [])
    .map((item) => ({
      value: stripHtml(item.titleHtml),
      label: stripHtml(item.bodyHtml),
    }))
    .filter((stat) => stat.value || stat.label);
}

function buildNavLookup(processNav?: ProcessNavGroup | null) {
  return new Map((processNav?.navItems ?? []).map((item) => [item.anchorId, item]));
}

function buildFallbackSteps(t: TranslateFn): ProcessStageView[] {
  return Array.from({ length: 6 }, (_, index) => {
    const n = index + 1;
    const id = n;
    return {
      id,
      sectionId: `section${n}`,
      stepNumber: String(n).padStart(2, '0'),
      title: t(`step${n}.title`),
      subtitle: t(`step${n}.phase`),
      color: STAGE_COLORS[index] ?? STAGE_COLORS[0],
      shortDesc: t(`step${n}.intro`),
      detailDesc: [
        t(`step${n}.detail1Title`),
        t(`step${n}.detail1Body`),
        t(`step${n}.detail2Title`),
        t(`step${n}.detail2Body`),
      ].join(' '),
      stats: getStageStats(index),
      videoSrc: getStageVideoSrc(id),
      imageUrl: null,
      buttonText: null,
    };
  });
}

export function mapProcessStages(
  steps: ProcessSequenceStep[] | undefined,
  processNav: ProcessNavGroup | null | undefined,
  t: TranslateFn,
): ProcessStageView[] {
  if (!steps || steps.length === 0) {
    return buildFallbackSteps(t);
  }

  const navByAnchor = buildNavLookup(processNav);

  return steps.map((step, index) => {
    const id = index + 1;
    const navItem = navByAnchor.get(step.sectionId);
    const intro = stripHtml(step.introHtml);
    const modalContent = stripHtml(step.modalContentHtml ?? '');

    return {
      id,
      sectionId: step.sectionId || `section${id}`,
      stepNumber: navItem?.stepNumber ?? String(id).padStart(2, '0'),
      title: step.title,
      subtitle: navItem?.title || step.title,
      color: STAGE_COLORS[index] ?? STAGE_COLORS[0],
      shortDesc: intro,
      detailDesc: modalContent || intro,
      stats: buildStatsFromItems(step.items),
      videoSrc: step.mediaUrl ?? '',
      imageUrl: step.mediaUrl,
      buttonText: step.buttonText,
    };
  });
}
